import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

const s:any = {
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 2,
  },
  picker: {
    p: 2,
    border: '1px dashed #c9d5ea',
    borderRadius: 2,
    backgroundColor: '#f8fbff',
  },
  previewWrap: {
    p: 1.5,
    borderRadius: 2,
    border: '1px solid #e5ecf7',
    maxWidth: 360,
  },
  preview: {
    width: '100%',
    borderRadius: 8,
    objectFit: 'cover',
    maxHeight: 260,
  },
  helper: {
    color: '#6b7280',
    fontSize: 13,
  },
  cameraPreview: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 12,
    backgroundColor: '#0f172a',
  },
};

class PhotoPanel extends Component<any, any> {
  static propTypes: any;
  static defaultProps: any;
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  stream: MediaStream | null;

  constructor(props: any) {
    super(props);
    this.videoRef = React.createRef<HTMLVideoElement>();
    this.canvasRef = React.createRef<HTMLCanvasElement>();
    this.stream = null;
    this.state = {
      previewUrl: '',
      cameraOpen: false,
      cameraError: '',
      isStartingCamera: false,
      isCapturing: false,
    };
  }

  componentDidMount() {
    const photo = this.props?.data?.photo;
    this.setState({ previewUrl: photo?.previewUrl || '' });
    this.syncValidity(photo);
  }

  componentWillUnmount() {
    this.stopCamera();
    const { previewUrl } = this.state;
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  }

  syncValidity = (photo: any) => {
    // Photo step is optional.
    if (typeof this.props.setValid === 'function') this.props.setValid(true);
  };

  setPhoto = (photo: any) => {
    this.props.setItem('data', 'photo', photo);
    const newdata = { ...(this.props?.data?.data || {}), img: photo?.previewUrl || '' };
    this.props.setItem('data', 'data', newdata);
    this.syncValidity(photo);
  };

  revokePreview = (previewUrl: string) => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  onFileChange = (event: any) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const oldPreview = this.state.previewUrl;
    this.revokePreview(oldPreview);

    const previewUrl = URL.createObjectURL(file);
    const photo = {
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      previewUrl,
      source: 'upload',
    };

    this.setState({ previewUrl });
    this.setPhoto(photo);
  };

  stopCamera = () => {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    const video = this.videoRef.current;
    if (video) {
      video.srcObject = null;
    }
  };

  openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.setState({
        cameraOpen: true,
        cameraError: 'This browser does not support webcam capture.',
        isStartingCamera: false,
      });
      return;
    }

    this.stopCamera();
    this.setState({ cameraOpen: true, cameraError: '', isStartingCamera: true });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
        },
        audio: false,
      });

      this.stream = stream;
      const video = this.videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play();
      }

      this.setState({ isStartingCamera: false });
    }
    catch (error:any) {
      this.stopCamera();
      this.setState({
        cameraError: error?.message || 'Unable to access the webcam. Check camera permissions and try again.',
        isStartingCamera: false,
      });
    }
  };

  closeCamera = () => {
    this.stopCamera();
    this.setState({
      cameraOpen: false,
      cameraError: '',
      isStartingCamera: false,
      isCapturing: false,
    });
  };

  capturePhoto = () => {
    const video = this.videoRef.current;
    const canvas = this.canvasRef.current;
    if (!video || !canvas) {
      this.setState({ cameraError: 'Camera preview is not ready yet.' });
      return;
    }

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) {
      this.setState({ cameraError: 'Camera preview is not ready yet.' });
      return;
    }

    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      this.setState({ cameraError: 'Unable to capture image from webcam.' });
      return;
    }

    context.drawImage(video, 0, 0, width, height);
    this.setState({ isCapturing: true, cameraError: '' });

    canvas.toBlob((blob) => {
      if (!blob) {
        this.setState({ isCapturing: false, cameraError: 'Unable to capture image from webcam.' });
        return;
      }

      const oldPreview = this.state.previewUrl;
      this.revokePreview(oldPreview);

      const fileName = `member-webcam-${Date.now()}.jpg`;
      const file = new File([blob], fileName, { type: 'image/jpeg' });
      const previewUrl = URL.createObjectURL(file);
      const photo = {
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        previewUrl,
        source: 'camera',
      };

      this.setState({ previewUrl, isCapturing: false });
      this.setPhoto(photo);
      this.closeCamera();
    }, 'image/jpeg', 0.92);
  };

  onClear = () => {
    const { previewUrl } = this.state;
    this.revokePreview(previewUrl);
    this.setState({ previewUrl: '' });
    this.setPhoto(null);
  };

  render() {
    const { previewUrl, cameraOpen, cameraError, isStartingCamera, isCapturing } = this.state;
    const photo = this.props?.data?.photo;
    return (
      <Box sx={{ ...s.root }}>
        <Box sx={{ ...s.picker }}>
          <Typography variant="subtitle1">Profile Photo</Typography>
          <Typography sx={{ ...s.helper, mt: 0.5 }}>
            Upload a clear portrait image or take one with your webcam (JPG/PNG/JPEG, max 10MB).
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" component="label">
              Choose Photo
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={this.onFileChange}
              />
            </Button>
            <Button variant="outlined" onClick={this.openCamera}>
              Use Webcam
            </Button>
            <Button variant="outlined" color="inherit" onClick={this.onClear} disabled={!photo?.file}>
              Remove
            </Button>
            <Typography sx={{ ...s.helper }}>
              {photo?.name || 'No file selected'}
            </Typography>
          </Box>
        </Box>
        {previewUrl && (
          <Box sx={{ ...s.previewWrap }}>
            <img src={previewUrl} alt="Member profile preview" style={s.preview} />
          </Box>
        )}
        <Dialog
          open={cameraOpen}
          onClose={this.closeCamera}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Take Profile Photo</DialogTitle>
          <DialogContent>
            <Typography sx={{ ...s.helper, mb: 2 }}>
              Position the member clearly in the frame, then capture the photo.
            </Typography>
            {cameraError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {cameraError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <video
                ref={this.videoRef}
                autoPlay
                playsInline
                muted
                style={s.cameraPreview}
              />
            </Box>
            <canvas ref={this.canvasRef} style={{ display: 'none' }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeCamera} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={this.capturePhoto}
              variant="contained"
              disabled={!!cameraError || isStartingCamera || isCapturing}
            >
              {isCapturing ? 'Capturing...' : 'Capture Photo'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

PhotoPanel.propTypes = {
  classes: PropTypes.object,
};

export default PhotoPanel;
